/**
 * VistoeraTASK-00546 — pure presentation rules for the Platform Admin Tenant
 * List "团队状态 / 授权方式 / 状态" cells. The Team state itself is computed by the
 * backend (`team_service.derive_team_state`); this module ONLY maps the
 * canonical values to Chinese labels / tone and decides which manual
 * entitlement action is legal — it never re-derives team state from
 * `member_count` (member count is a fact, not an entitlement).
 */
import type { TeamEntitlementSource, TeamState, WorkspaceWithCounts } from '../types'

export type Tone = 'green' | 'purple' | 'blue' | 'gold' | 'red' | 'default'

export const TEAM_STATE_LABELS: Record<TeamState, string> = {
  personal: '个人',
  entitled_pending: '已授权未组队',
  team: '团队',
}

export const TEAM_STATE_TONES: Record<TeamState, Tone> = {
  personal: 'default',
  entitled_pending: 'blue',
  team: 'green',
}

export const ENTITLEMENT_SOURCE_LABELS: Record<TeamEntitlementSource | 'none', string> = {
  credit_threshold: '积分达标',
  platform_admin: '人为指定',
  plan: '套餐包含',
  unknown: '来源未记录',
  none: '未授权',
}

export const ENTITLEMENT_SOURCE_TONES: Record<TeamEntitlementSource | 'none', Tone> = {
  credit_threshold: 'purple',
  platform_admin: 'blue',
  plan: 'gold',
  unknown: 'default',
  none: 'default',
}

export const WORKSPACE_STATUS_LABELS: Record<string, string> = {
  active: '正常',
  suspended: '已暂停',
  expired: '已到期',
}

export function workspaceStatusLabel(status: string): string {
  return WORKSPACE_STATUS_LABELS[status] ?? status
}

export function workspaceStatusTone(status: string): Tone {
  if (status === 'active') return 'green'
  if (status === 'suspended') return 'red'
  if (status === 'expired') return 'gold'
  return 'default'
}

/** Badges for the 授权方式 column: one per effective source, or a single 未授权. */
export function entitlementBadges(row: Pick<WorkspaceWithCounts, 'team_entitlement_sources'>): Array<{
  key: TeamEntitlementSource | 'none'
  label: string
  tone: Tone
}> {
  const sources = row.team_entitlement_sources ?? []
  const keys: Array<TeamEntitlementSource | 'none'> = sources.length > 0 ? sources : ['none']
  return keys.map((key) => ({ key, label: ENTITLEMENT_SOURCE_LABELS[key] ?? String(key), tone: ENTITLEMENT_SOURCE_TONES[key] ?? 'default' }))
}

export type ManualEntitlementAction =
  | { kind: 'grant' }
  | { kind: 'revoke' }
  /** Entitled via a non-manual source: a manual grant/revoke is not applicable. */
  | { kind: 'none'; reason: string }

/**
 * Which manual action the Platform Admin may take. A manual revoke is only
 * offered when the CURRENT effective source is `platform_admin`; an entitlement
 * that comes from points / plan is never withdrawn (nor overridden) manually —
 * the server enforces the same rule with 409.
 */
export function manualEntitlementAction(
  row: Pick<WorkspaceWithCounts, 'team_mode_enabled' | 'team_entitlement_sources'>,
): ManualEntitlementAction {
  if (!row.team_mode_enabled) return { kind: 'grant' }
  const sources = row.team_entitlement_sources ?? []
  if (sources.includes('platform_admin')) return { kind: 'revoke' }
  const labels = sources.map((s) => ENTITLEMENT_SOURCE_LABELS[s] ?? s).join(' / ') || '其他来源'
  return { kind: 'none', reason: `团队功能资格由「${labels}」授予，无法人工授予或撤销。` }
}

/** Alias of site-wide integer credit display (VistoeraTASK-00745). */
export { formatCredits as formatPoints } from '../ui/creditsFormat'

/**
 * TASK-00667 — display label for Workspace Effective Plan.
 * Uses catalog `plan_display_name` via `plan_key` only — never a hardcoded
 * free/standard/premium name map (admins rename/add plans dynamically).
 */
export type TenantPlanCell = {
  label: string
  kind: 'ok' | 'disabled' | 'missing' | 'orphan'
}

export function tenantPlanCell(
  row: Pick<WorkspaceWithCounts, 'plan_key' | 'plan_display_name' | 'plan_enabled'>,
): TenantPlanCell {
  if (!row.plan_key) return { label: '未配置', kind: 'missing' }
  if (!row.plan_display_name) return { label: '套餐异常', kind: 'orphan' }
  if (row.plan_enabled === false) return { label: row.plan_display_name, kind: 'disabled' }
  return { label: row.plan_display_name, kind: 'ok' }
}
